<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="캠페인 만들기"
        subtitle="나만의 전용 상담 링크 생성"
      >
        <template #actions>
          <div class="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl">
            <svg class="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="font-semibold text-amber-600">{{ pointBalance.toLocaleString() }} 포인트</span>
          </div>
        </template>
      </PartnersHeader>
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 내 현장 정보 없음 -->
        <div v-if="!myProperty" class="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <div>
              <p class="font-semibold text-amber-800">등록된 현장이 없습니다</p>
              <p class="text-sm text-amber-600">현장 등록 후 캠페인을 생성할 수 있습니다.</p>
            </div>
          </div>
        </div>

        <!-- 이미 활성 캠페인이 있는 경우 -->
        <div v-else-if="activeCampaign" class="bg-violet-50 border border-violet-200 rounded-2xl p-6">
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="font-bold text-violet-800 mb-1">이미 활성 캠페인이 있습니다</h3>
              <p class="text-[13px] text-violet-600 mb-3">1인당 1개의 캠페인만 운영할 수 있습니다. 기존 캠페인을 삭제하거나 만료 후 새로 생성해주세요.</p>

              <div class="bg-white rounded-xl p-4 border border-violet-200">
                <div class="flex items-center justify-between mb-2">
                  <span class="font-semibold text-slate-800">{{ activeCampaign.propertyName }}</span>
                  <span class="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">활성</span>
                </div>
                <div class="text-[13px] text-slate-500 space-y-1">
                  <p>기간: {{ activeCampaign.startDate }} ~ {{ activeCampaign.endDate }}</p>
                  <p>남은 기간: D-{{ getRemainingDays(activeCampaign.endDate) }}</p>
                </div>
              </div>

              <router-link
                to="/partners/ad/my"
                class="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-xl hover:bg-violet-700 transition"
              >
                내 캠페인 관리하기
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </router-link>
            </div>
          </div>
        </div>

        <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 현장 정보 + 바로가기 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 현장 정보 카드 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5">
              <p class="text-[12px] font-bold text-slate-500 mb-3">내 현장</p>
              <div class="flex items-center gap-4">
                <div class="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                  <svg class="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
                <div>
                  <h3 class="text-[15px] font-bold text-slate-800">{{ myProperty.propertyName }}</h3>
                  <p class="text-[13px] text-slate-500">이 현장의 캠페인을 생성합니다</p>
                </div>
              </div>
            </div>

            <!-- 캠페인 안내 -->
            <div class="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <p class="text-[12px] font-bold text-emerald-700 mb-3">캠페인이란?</p>
              <ul class="space-y-2 text-[13px] text-emerald-700">
                <li class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>전용 링크로 유입된 고객은 <strong>오직 나에게만</strong> 상담 신청</span>
                </li>
                <li class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span><strong>선착순 줍기 없음</strong> - 24시간 제한 없이 영구 보관</span>
                </li>
                <li class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>SNS, 블로그, 명함 등에 링크를 공유</span>
                </li>
                <li class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span>광고 유입 콜은 <strong class="text-amber-600">건당 10,000P</strong>에 구매</span>
                </li>
              </ul>
            </div>

            <!-- 바로가기 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-4">
              <p class="text-[12px] font-bold text-slate-500 mb-3">바로가기</p>
              <div class="space-y-2">
                <router-link to="/partners/ad/my" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">내 캠페인 목록</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link to="/partners/point" class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <span class="text-[13px] font-medium text-slate-700">포인트 충전하기</span>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 기간 선택 + 결제 -->
          <div class="lg:col-span-2 space-y-4">
            <!-- 기간 선택 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5">
              <h2 class="text-[15px] font-bold text-slate-800 mb-2">기간 선택</h2>
              <p class="text-[13px] text-slate-500 mb-4">최소 2주부터 최대 3개월까지 선택 가능합니다.</p>

              <div class="grid grid-cols-2 gap-3">
                <button
                  v-for="(option, key) in pricingOptions"
                  :key="key"
                  @click="selectedDuration = key"
                  :class="[
                    'p-4 rounded-xl border-2 transition text-left relative overflow-hidden',
                    selectedDuration === key
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-300'
                  ]"
                >
                  <!-- 할인 배지 -->
                  <span v-if="option.discount" class="absolute top-0 right-0 px-2 py-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-bl-lg">
                    {{ option.discount }}% OFF
                  </span>
                  <p class="font-semibold text-slate-800">{{ option.label }}</p>
                  <p class="text-sm text-slate-500">{{ option.days }}일</p>
                  <div class="mt-2">
                    <span v-if="option.originalPrice" class="text-sm text-slate-400 line-through mr-2">{{ option.originalPrice.toLocaleString() }}</span>
                    <span class="text-lg font-bold text-emerald-600">{{ option.price.toLocaleString() }} 포인트</span>
                  </div>
                </button>
              </div>
            </div>

            <!-- 결제 정보 -->
            <div v-if="selectedDuration" class="bg-white border border-slate-200 rounded-2xl p-5">
              <h2 class="text-[15px] font-bold text-slate-800 mb-4">결제 정보</h2>

              <div class="space-y-3">
                <div class="flex justify-between text-slate-600 text-[14px]">
                  <span>선택 기간</span>
                  <span>{{ pricingOptions[selectedDuration].label }}</span>
                </div>
                <div class="flex justify-between text-slate-600 text-[14px]">
                  <span>유효 기간</span>
                  <span>{{ pricingOptions[selectedDuration].days }}일</span>
                </div>
                <div v-if="pricingOptions[selectedDuration].originalPrice" class="flex justify-between text-slate-400 text-[14px]">
                  <span>정가</span>
                  <span class="line-through">{{ pricingOptions[selectedDuration].originalPrice.toLocaleString() }} 포인트</span>
                </div>
                <div v-if="pricingOptions[selectedDuration].discount" class="flex justify-between text-rose-500 text-[14px]">
                  <span>할인</span>
                  <span>-{{ (pricingOptions[selectedDuration].originalPrice - pricingOptions[selectedDuration].price).toLocaleString() }} 포인트 ({{ pricingOptions[selectedDuration].discount }}%)</span>
                </div>
                <div class="flex justify-between pt-3 border-t border-slate-200">
                  <span class="font-semibold text-slate-800">결제 금액</span>
                  <span class="text-xl font-bold text-emerald-600">{{ pricingOptions[selectedDuration].price.toLocaleString() }} 포인트</span>
                </div>
              </div>

              <div v-if="pricingOptions[selectedDuration].price > pointBalance" class="mt-4 p-3 bg-red-50 rounded-xl text-red-600 text-sm">
                포인트이 부족합니다. 충전 후 다시 시도해주세요.
              </div>
            </div>

            <!-- 생성 버튼 -->
            <button
              v-if="selectedDuration"
              @click="handleCreate"
              :disabled="creating || pricingOptions[selectedDuration].price > pointBalance"
              class="w-full py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ creating ? '생성 중...' : '캠페인 생성하기' }}
            </button>
          </div>
        </div>

        <!-- 생성 완료 모달 -->
        <div v-if="showSuccessModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div class="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div class="p-6 text-center">
              <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-800 mb-2">캠페인 생성 완료!</h3>
              <p class="text-slate-500 mb-4">전용 링크가 생성되었습니다.</p>

              <div class="bg-slate-50 rounded-xl p-4 mb-4">
                <p class="text-sm text-slate-500 mb-2">캠페인 링크</p>
                <div class="flex items-center gap-2">
                  <input
                    type="text"
                    :value="createdUrl"
                    readonly
                    class="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  />
                  <button
                    @click="copyLink"
                    class="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
                  >
                    복사
                  </button>
                </div>
              </div>
            </div>

            <div class="px-6 pb-6 flex gap-3">
              <button
                @click="goToList"
                class="flex-1 py-3 border border-slate-300 text-slate-600 font-medium rounded-xl hover:bg-slate-50"
              >
                내 캠페인 보기
              </button>
              <button
                @click="closeModal"
                class="flex-1 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'
import { usePoint } from '@/composables/partners/usePoint'
import { useCampaign } from '@/composables/partners/useCampaign'
import { useAuth } from '@/composables/common/useAuth'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/firebase/firebase'

export default {
  name: 'CampaignCreateView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const router = useRouter()
    const { user } = useAuth()
    const { balance: pointBalance, loadBalance } = usePoint()
    const { getPricing, createCampaign, getActiveCampaign, getRemainingDays } = useCampaign()

    const myProperty = ref(null)
    const activeCampaign = ref(null)
    const selectedDuration = ref(null)
    const creating = ref(false)
    const showSuccessModal = ref(false)
    const createdUrl = ref('')

    const pricingOptions = computed(() => getPricing())

    // 활성 캠페인 확인
    const checkActiveCampaign = async () => {
      activeCampaign.value = await getActiveCampaign()
    }

    // 내 담당 현장 로드
    const loadMyProperty = async () => {
      if (!user.value) return

      try {
        const q = query(
          collection(db, 'propertyPartners'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'active')
        )
        const snapshot = await getDocs(q)

        if (!snapshot.empty) {
          myProperty.value = {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
          }
        }
      } catch (err) {
        console.error('현장 로드 실패:', err)
      }
    }

    const handleCreate = async () => {
      if (!selectedDuration.value || creating.value) return

      if (!confirm('캠페인을 생성하시겠습니까?')) return

      creating.value = true

      const result = await createCampaign(
        myProperty.value.propertyId,
        myProperty.value.propertyName,
        selectedDuration.value
      )

      creating.value = false

      if (result.success) {
        createdUrl.value = window.location.origin + result.url
        showSuccessModal.value = true
        await loadBalance()
      } else {
        alert('생성 실패: ' + result.error)
      }
    }

    const copyLink = async () => {
      try {
        await navigator.clipboard.writeText(createdUrl.value)
        alert('링크가 복사되었습니다!')
      } catch {
        alert('복사에 실패했습니다. 직접 복사해주세요.')
      }
    }

    const goToList = () => {
      router.push('/partners/ad/my')
    }

    const closeModal = () => {
      showSuccessModal.value = false
      selectedDuration.value = null
    }

    onMounted(async () => {
      await loadBalance()
      await loadMyProperty()
      await checkActiveCampaign()
    })

    return {
      pointBalance,
      myProperty,
      activeCampaign,
      pricingOptions,
      selectedDuration,
      creating,
      showSuccessModal,
      createdUrl,
      handleCreate,
      copyLink,
      goToList,
      closeModal,
      getRemainingDays
    }
  }
}
</script>
